import { assignMedalTiers, isMedalEligibleDate, type MedalTier } from "@dailywars/core/medals";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef } from "react";
import type * as React from "react";

import { CardContent } from "#/components/ui/card";
import { InfotipProvider } from "#/components/ui/infotip";
import { ScrollArea } from "#/components/ui/scroll-area";
import { Spinner } from "#/components/ui/spinner";

import { LeaderboardVirtualRow, type LeaderboardRow } from "./-leaderboard.$mapId.row";

const ROW_ESTIMATE_PX = 48;

function useLeaderboardPrefetchNextPage({
  scrollViewportRef,
  virtualItems,
  flatRowsLength,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  fetchMoreThresholdRows,
}: {
  scrollViewportRef: React.RefObject<HTMLDivElement | null>;
  virtualItems: ReturnType<
    ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>["getVirtualItems"]
  >;
  flatRowsLength: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  fetchMoreThresholdRows: number;
}) {
  const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index;
  useEffect(() => {
    if (!scrollViewportRef.current) return;
    if (lastVisibleIndex === undefined) return;
    if (
      lastVisibleIndex >= flatRowsLength - fetchMoreThresholdRows &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      void fetchNextPage();
    }
  }, [
    scrollViewportRef,
    lastVisibleIndex,
    flatRowsLength,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    fetchMoreThresholdRows,
  ]);
}

function LeaderboardVirtualRows({
  flatRows,
  medalsByPlayerId,
  rowVirtualizer,
  virtualItems,
  viewerPlayerId,
  medalsVisible,
  variantLabel,
  showReplayLinks,
}: {
  flatRows: LeaderboardRow[];
  medalsByPlayerId: Map<string, MedalTier>;
  rowVirtualizer: ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>;
  virtualItems: ReturnType<
    ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>["getVirtualItems"]
  >;
  viewerPlayerId: string | undefined;
  medalsVisible: boolean;
  variantLabel: "Standard" | "Premium";
  showReplayLinks: boolean;
}) {
  return (
    <div className="relative w-full" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
      {virtualItems.map((virtualRow) => {
        const entry = flatRows[virtualRow.index];
        if (!entry) return null;
        return (
          <LeaderboardVirtualRow
            key={virtualRow.key}
            virtualRow={virtualRow}
            entry={entry}
            medal={medalsByPlayerId.get(entry.playerId)}
            viewerPlayerId={viewerPlayerId}
            medalsVisible={medalsVisible}
            variantLabel={variantLabel}
            showReplayLinks={showReplayLinks}
          />
        );
      })}
    </div>
  );
}

function LeaderboardScrollList({
  scrollViewportRef,
  flatRows,
  medalsByPlayerId,
  rowVirtualizer,
  virtualItems,
  viewerPlayerId,
  medalsVisible,
  variantLabel,
  showReplayLinks,
}: {
  scrollViewportRef: React.RefObject<HTMLDivElement | null>;
  flatRows: LeaderboardRow[];
  medalsByPlayerId: Map<string, MedalTier>;
  rowVirtualizer: ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>;
  virtualItems: ReturnType<
    ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>["getVirtualItems"]
  >;
  viewerPlayerId: string | undefined;
  medalsVisible: boolean;
  variantLabel: "Standard" | "Premium";
  showReplayLinks: boolean;
}) {
  return (
    <CardContent className="p-0 text-left">
      <InfotipProvider delay={300}>
        <ScrollArea
          viewportRef={scrollViewportRef}
          className="h-[min(70vh,560px)] rounded-[inherit]"
        >
          <LeaderboardVirtualRows
            flatRows={flatRows}
            medalsByPlayerId={medalsByPlayerId}
            rowVirtualizer={rowVirtualizer}
            virtualItems={virtualItems}
            viewerPlayerId={viewerPlayerId}
            medalsVisible={medalsVisible}
            variantLabel={variantLabel}
            showReplayLinks={showReplayLinks}
          />
        </ScrollArea>
      </InfotipProvider>
    </CardContent>
  );
}

function LeaderboardFetchMoreFooter() {
  return (
    <div className="flex items-center justify-center gap-2 border-t border-border py-3 text-xs text-muted-foreground">
      <Spinner />
      <span>Loading more…</span>
    </div>
  );
}

function useLeaderboardListState(flatRows: LeaderboardRow[]) {
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const medalsByPlayerId = useMemo(() => assignMedalTiers(flatRows), [flatRows]);
  const rowVirtualizer = useVirtualizer({
    count: flatRows.length,
    getScrollElement: () => scrollViewportRef.current,
    estimateSize: () => ROW_ESTIMATE_PX,
    overscan: 12,
  });
  return {
    scrollViewportRef,
    medalsByPlayerId,
    rowVirtualizer,
    virtualItems: rowVirtualizer.getVirtualItems(),
  };
}

export function LeaderboardFilledBody({
  mapDate,
  viewerPlayerId,
  flatRows,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  variantLabel,
  showReplayLinks,
}: {
  mapDate: string;
  viewerPlayerId: string | undefined;
  flatRows: LeaderboardRow[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  variantLabel: "Standard" | "Premium";
  showReplayLinks: boolean;
}) {
  const medalsVisible = isMedalEligibleDate(mapDate);
  const { scrollViewportRef, medalsByPlayerId, rowVirtualizer, virtualItems } =
    useLeaderboardListState(flatRows);
  useLeaderboardPrefetchNextPage({
    scrollViewportRef,
    virtualItems,
    flatRowsLength: flatRows.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    fetchMoreThresholdRows: 5,
  });

  return (
    <>
      <LeaderboardScrollList
        scrollViewportRef={scrollViewportRef}
        flatRows={flatRows}
        medalsByPlayerId={medalsByPlayerId}
        rowVirtualizer={rowVirtualizer}
        virtualItems={virtualItems}
        viewerPlayerId={viewerPlayerId}
        medalsVisible={medalsVisible}
        variantLabel={variantLabel}
        showReplayLinks={showReplayLinks}
      />
      {isFetchingNextPage ? <LeaderboardFetchMoreFooter /> : null}
    </>
  );
}

export { LeaderboardEmptyBody, LeaderboardMapHero } from "./-leaderboard.$mapId.shell";
